<template>
  <v-container fluid>

    <PageTitle icon="fas fa-fw fa-envelope">
      New Email Template
    </PageTitle>

    <v-form @submit.prevent="submit"
            :disabled="form.busy">
      <ShareTemplateForm v-if="$root.isAdminRoute"
                         :form="form">
      </ShareTemplateForm>
      <MemberShareTemplateForm v-else
                               :form="form">
      </MemberShareTemplateForm>

      <v-card>
        <v-card-actions class="px-4">
          <v-btn type="submit"
                 color="primary"
                 :disabled="form.busy">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {isObject} from 'lodash';
import moment from 'moment-timezone';
import shareTemplateService from '../../../../ml/services/shareTemplateService';
import ShareTemplateForm from './components/ShareTemplateForm';
import MemberShareTemplateForm from "./library/MemberShareTemplateForm";
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, MemberShareTemplateForm, ShareTemplateForm},
  data() {
    return {
      form: new Form(shareTemplateService.initForm()),
      sourceTemplate: null,
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isMember',
      'team',
      'user',
      'users',
    ]),
    ...mapGetters([
      'folders',
    ]),
    sourceID() {
      if (!this.$route.query.source) {
        return false;
      }
      return parseInt(this.$route.query.source);
    },
    templateID() {
      return parseInt(this.$route.params.templateID);
    },
    isAccessEveryone() {
      return this.form.accessControl === 'Everyone';
    },
    isAccessGroups() {
      return this.form.accessControl === 'Groups';
    },
    isAccessUser() {
      return this.form.accessControl === 'User';
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (this.sourceID) {
        this.form.busy = true;
        shareTemplateService.getTemplate(this.sourceID)
          .then(data => {
            this.sourceTemplate = data;
            this.populateFromSource();
            this.configureForLibrary();
          })
          .catch(() => {
          })
          .finally(() => {
            this.form.busy = false;
          })
      } else if (false === this.$root.isAdminRoute) {
        this.configureForLibrary();
      }
    },
    configureForLibrary() { // MLD-3150
      if (false === this.$root.isAdminRoute) {
        this.form.folder_id = null;
        this.form.asset_id = null;
        if (this.form.name) {
          this.form.name = `${this.form.name} - ${moment().format('YYYY-MM-DD')} - ${moment().format('X')}`
        } else {
          this.form.name = `${this.user.name} - Template - ${moment().format('X')}`
        }
        this.form.user_id = this.user.id;
        this.form.groups = [];
        this.form.is_featured = false;
        this.form.can_be_copied = true;
        this.form.is_quick_share = false;
        this.form.is_default = false;
        this.form.editor = 'tinymce';
        this.form.is_subject_editable = true;
      }
    },
    async populateFromSource() {
      if (this.sourceTemplate
        && this.sourceTemplate.can_be_copied) {

        this.form.source_template_id = this.sourceTemplate.id;
        this.form.subject = this.sourceTemplate.subject;
        this.form.is_subject_editable = this.sourceTemplate.is_subject_editable;

        // MLD-6517
        const subjectLines = await shareTemplateService.getSubjectLinesFor(this.sourceTemplate.id);
        if (subjectLines.length > 0) {
          this.form.subjects = subjectLines.map(s => {
            return {
              id: null,
              position: s.position,
              subject: s.subject,
            }
          })
        } else {
          this.form.subjects[0].subject = this.form.subject;
        }

        this.form.editor = this.sourceTemplate.editor; // MLD-2473
        this.form.design = this.sourceTemplate.design; // MLD-2473
        this.form.html_body = this.sourceTemplate.html_body;
        this.form.status = 'Active';
        this.form.is_web = this.sourceTemplate.is_web;
        this.form.is_app = this.sourceTemplate.is_app;
        this.form.is_gmail = this.sourceTemplate.is_gmail;
        this.form.is_outlook = this.sourceTemplate.is_outlook;
        // MLD-2325
        this.form.name = `${this.sourceTemplate.name} - COPY`;
        this.form.user_id = this.sourceTemplate.user_id ? this.sourceTemplate.user_id : '';
        this.form.asset_id = this.sourceTemplate.asset_id;
        this.form.release_at = this.sourceTemplate.release_at;
        this.form.expires_at = this.sourceTemplate.expires_at;
        // MLD-2327
        this.form.folder_id = this.sourceTemplate.folder_id ? this.sourceTemplate.folder_id : '';

        this.form.groups = this.sourceTemplate.groups; // MLD-2482

        this.form.is_featured = this.sourceTemplate.is_featured;
        this.form.can_share_cdn_link = this.sourceTemplate.can_share_cdn_link;
        this.form.is_campaign = this.sourceTemplate.is_campaign; // MLD-2524
        this.form.can_be_copied = this.sourceTemplate.can_be_copied;

        this.form.is_quick_share = this.sourceTemplate.is_quick_share;
        this.form.is_default = this.sourceTemplate.is_default;

        this.form.sanitize_attachment_name = this.sourceTemplate.sanitize_attachment_name;
      }
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      if (this.isAccessEveryone) { // MLD-4854
        // Clear the Groups and User
        this.form.groups = [];
        this.form.user_id = '';
      } else if (this.isAccessGroups) {
        // Clear the user
        this.form.user_id = '';
      } else if (this.isAccessUser) {
        // Clear the groups
        this.form.groups = [];
      }
      // MLD-2863
      const form = this.form.data();
      form.subject = form.subjects.length > 0 ? form.subjects[0].subject : null; // MLD-6517
      form.groups = form.groups.map(g => isObject(g) ? g.id : g);
      form.bcc_recipients = form.bcc_recipients ? form.bcc_recipients.split(',') : null; // MLD-5567
      form.cc_recipients = form.cc_recipients ? form.cc_recipients.split(',') : null; // MLD-5566
      this.form.post(`/api/share-templates`, {data: form})
        .then(response => {
          const template = response.data;
          this.$store.commit('setSuccessMessage', 'Template Created');
          /*if (this.form.folder_id) {
            this.$store.dispatch('setActiveFolderID', this.form.folder_id);
          } else {
            this.$store.dispatch('setActiveFolderID', -1);
          }*/
          this.$store.dispatch('setActiveFolderID', -1);
          this.$store.dispatch('fetchShareTemplates').then(); // MLD-6865
          if (template.is_unlayer) {
            this.$router.push({name: 'admin.share_template.unlayer.edit', params: {templateID: template.id}});
          } else if (this.$root.isAdminRoute) {
            this.$router.push({name: 'admin.share_template.edit', params: {templateID: template.id}});
          } else {
            this.$router.push({name: `${this.$root.routePrefix}.share_template.edit`, params: {templateID: template.id}});
          }
        })
        .catch(() => {
        });
    }
  }
}
</script>
